import { useRef, useState } from "react";
import { Midi } from "tonal";
import * as Tone from "tone";
import { useEffectOnce } from "usehooks-ts";
import { Note } from "../constants/notes";
import { logger } from "./logger";
import { piano } from "./sampleConfig";
import { PlayerExternalState } from "./useExternalState/useExternalState";
import useSyncSynthsWithExternalState from "./useSyncSynthsWithExternalState";

export interface TriggerAttackReleasePlaybackParams {
  midiNotes: number[];
  duration: string;
  degrees: number[];
}

export type SynthType = "melody" | "chord" | "metronome";

export interface TriggerAttackRelease {
  time: number;
  metrome: boolean;
  melody: TriggerAttackReleasePlaybackParams | undefined;
  chord: TriggerAttackReleasePlaybackParams | undefined;
}

interface Params {
  state: PlayerExternalState;
}

export default function useSynths({ state }: Params) {
  const melody = useRef<Tone.Sampler>();
  const chord = useRef<Tone.Sampler>();
  const metronome = useRef<Tone.MembraneSynth>();
  const neededLoadCount = useRef(0);
  const [loadCount, setLoadCount] = useState(0);

  function getMelodySynth() {
    if (melody.current) {
      return melody.current;
    }

    neededLoadCount.current++;

    melody.current = new Tone.Sampler(
      samplerParams(() => {
        setLoadCount((prev) => prev + 1);
      })
    ).toDestination();

    return melody.current;
  }

  function getChordSynth() {
    if (chord.current) {
      return chord.current;
    }

    neededLoadCount.current++;

    chord.current = new Tone.Sampler(
      samplerParams(() => {
        setLoadCount((prev) => prev + 1);
      })
    ).toDestination();

    return chord.current;
  }

  function getMetronomeSynth() {
    if (metronome.current) {
      return metronome.current;
    }

    metronome.current = new Tone.MembraneSynth({
      pitchDecay: 0.005,
    }).toDestination();

    return metronome.current;
  }

  useEffectOnce(() => {
    logger("useSynths: INIT LOAD ALL SYNTHS ONCE");

    getMelodySynth();
    getChordSynth();
    getMetronomeSynth();
  });

  useSyncSynthsWithExternalState({
    state,
    synths: () => ({
      melody: getMelodySynth(),
      chord: getChordSynth(),
      metronome: getMetronomeSynth(),
    }),
  });

  return {
    loaded: loadCount > 0 && loadCount === neededLoadCount.current,
    playMelody(midiNotes: number[]) {
      getMelodySynth().triggerAttackRelease(
        midiNotes.map(convertMidiToFrequencyClass),
        "4n",
        Tone.now()
      );
    },
    releaseAll() {
      getMelodySynth().releaseAll();
      getChordSynth().releaseAll();
      getMetronomeSynth().triggerRelease();
    },
    triggerAttackRelease(params: TriggerAttackRelease) {
      logger("TRIGGER ATTACK RELEASE", params);

      if (params.metrome) {
        getMetronomeSynth().triggerAttack(
          convertMidiToFrequencyClass(Midi.toMidi(Note.c2)!),
          params.time
        );
      }

      if (params.melody) {
        getMelodySynth().triggerAttackRelease(
          params.melody.midiNotes.map(convertMidiToFrequencyClass),
          params.melody.duration,
          params.time
        );
      }

      if (params.chord) {
        getChordSynth().triggerAttackRelease(
          params.chord.midiNotes.map(convertMidiToFrequencyClass),
          params.chord.duration,
          params.time
        );
      }
    },
  };
}

function convertMidiToFrequencyClass(midi: number): Tone.Unit.Frequency {
  return Tone.Frequency(midi, "midi").toFrequency();
}

// const getUrlsByOctave = (octave: number) => ({
//   ["A" + octave]: `${Note as TonalNote.get("a" + octave).midi}.mp3`,
//   ["A#" + octave]: `${Note as TonalNote.get("a#" + octave).midi}.mp3`,
//   ["B" + octave]: `${Note as TonalNote.get("b" + octave).midi}.mp3`,
//   ["C" + octave]: `${Note as TonalNote.get("c" + octave).midi}.mp3`,
//   ["C#" + octave]: `${Note as TonalNote.get("c#" + octave).midi}.mp3`,
//   ["D" + octave]: `${Note as TonalNote.get("d" + octave).midi}.mp3`,
//   ["D#" + octave]: `${Note as TonalNote.get("d#" + octave).midi}.mp3`,
//   ["E" + octave]: `${Note as TonalNote.get("e" + octave).midi}.mp3`,
//   ["F" + octave]: `${Note as TonalNote.get("f" + octave).midi}.mp3`,
//   ["F#" + octave]: `${Note as TonalNote.get("f#" + octave).midi}.mp3`,
//   ["G" + octave]: `${Note as TonalNote.get("g" + octave).midi}.mp3`,
//   ["G#" + octave]: `${Note as TonalNote.get("g#" + octave).midi}.mp3`,
// });

const sample = piano;

const samplerParams: (onload: () => void) => Partial<Tone.SamplerOptions> = (
  onload
) => ({
  urls: sample.samples,
  release: 0.2,
  attack: 0.1,
  baseUrl: `https://storage.googleapis.com/bedroom-producer-samples/samples/${sample.folder}/`,
  onload,
});
